import React, { useEffect, useMemo, useState } from 'react'
import './style.scss'
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { fetchUserBrands as fetchUserBrandsThunk } from 'slices/users/brands/thunk';
import { UserBrand } from 'types/users';
export interface AICampaignCreationLayoutStep {
    label: string;
    completed?: boolean;
    active?: boolean;
    onClick?: () => void;
    title: string;
    subtitle: string;
}

function getPlatformIcon(platform?: string) {
    if (platform === 'yt') return <i className="ri-youtube-fill" aria-hidden="true" />;
    if (platform === 'tt') return <i className="ri-tiktok-fill" aria-hidden="true" />;
    return <i className="ri-instagram-line" aria-hidden="true" />;
}

interface AICampaignCreationLayoutProps {
    children: React.ReactNode;
    steps: AICampaignCreationLayoutStep[];
    brandSocialAccountId: string | number;
    onBrandChange?: (brand: UserBrand | null) => void;
    bottomBarContent?: React.ReactNode;
    onBackClick?: () => void;
    mode?: 'prompt' | 'strategy';
}

const AICampaignCreationLayout: React.FC<AICampaignCreationLayoutProps> = ({ children, steps, brandSocialAccountId, onBrandChange, bottomBarContent, onBackClick, mode }) => {

    const activeStep = steps.find(step => step.active);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();
    const userBrandsWithoutStrategy = useSelector((state: RootState) => state.userBrands.items);
    const userBrandsWithStrategy = useSelector((state: RootState) => state.userBrands.items_with_strategy);
    const userBrands = mode === 'strategy' ? userBrandsWithStrategy : userBrandsWithoutStrategy;
    const [brandMenuOpen, setBrandMenuOpen] = useState(false);
    const brandMenuRef = React.useRef<HTMLDivElement | null>(null);

    const selectedBrand = useMemo(() => {
        return userBrands.find((b) => b.social_account?.uuid === brandSocialAccountId) ?? null;
    }, [userBrands, brandSocialAccountId]);

    const brandsWithSocial = useMemo(
        () => (userBrands ?? []).filter((ub) => Boolean(ub.social_account)),
        [userBrands]
    );



    useEffect(() => {
        if (!brandMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            const el = brandMenuRef.current;
            if (!el) return;
            if (e.target instanceof Node && el.contains(e.target)) return;
            setBrandMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [brandMenuOpen]);

    return (
        <div className="campaign-ai-creation">
            <div className="campaign-ai-creation__topbar">
                <span className="campaign-ai-creation__back cursor-pointer" aria-label="Back" onClick={onBackClick}>
                    <i className="ri-arrow-left-line fs-24" />
                </span>

                <div className="campaign-ai-creation__stepper" aria-label="Campaign builder steps">
                    {steps.map((step, index) => (
                        <>
                            <div key={index} className={`campaign-ai-step ${step.completed ? 'campaign-ai-step--completed' : ''} ${step.active ? ' campaign-ai-step--active' : ''}`}>
                                <span className="campaign-ai-step__circle">{step.completed ? (
                                    <span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="20" viewBox="0 0 23 20" fill="none">
                                            <path d="M1.375 11.275L7.1369 17.875L21.5417 1.375" stroke="white" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                ) : (index + 1)}</span>
                                <span className="campaign-ai-step__label">{step.label}</span>
                            </div>
                            {index < steps.length - 1 && (
                                <div className="campaign-ai-step__line" aria-hidden="true" />
                            )}
                        </>
                    ))}

                </div>
            </div>

            <div className="campaign-ai-creation__content">
                <div className="campaign-ai-creation__headerRow">
                    <div>
                        <div className="campaign-ai-creation__title">{activeStep?.title}</div>
                        <div className="campaign-ai-creation__subtitle">{activeStep?.subtitle}</div>
                    </div>

                    <div className="campaign-ai-brandDropdown" ref={brandMenuRef}>
                        <button
                            type="button"
                            className="campaign-ai-creation__brandSelect"
                            aria-label="Select brand"
                            aria-haspopup="listbox"
                            aria-expanded={brandMenuOpen}
                            onClick={() => setBrandMenuOpen((v) => !v)}
                        >
                            <span className="campaign-ai-creation__brandIcon" aria-hidden="true">
                                {selectedBrand?.social_account?.profile_picture_url ? (
                                    <img src={selectedBrand.social_account.profile_picture_url} alt="" />
                                ) : (
                                    getPlatformIcon(selectedBrand?.social_account?.platform)
                                )}
                            </span>
                            <span className="campaign-ai-creation__brandName">
                                {selectedBrand?.name ?? 'Select brand'}
                            </span>
                            <i className="ri-arrow-down-s-line fs-20 text-primary" aria-hidden="true" />
                        </button>

                        {brandMenuOpen && (
                            <div className="campaign-ai-brandDropdown__menu" role="listbox" aria-label="Brands">
                                {brandsWithSocial.length === 0 ? (
                                    <div className="campaign-ai-brandDropdown__empty">No brands with connected social accounts.</div>
                                ) : (
                                    brandsWithSocial.map((b) => {
                                        const isSelected = b.social_account?.uuid === brandSocialAccountId;
                                        const handleRaw = b.social_account?.handle ?? '';
                                        const handle = handleRaw ? (handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`) : '';
                                        const avatarUrl = b.social_account?.profile_picture_url ?? '';
                                        return (
                                            <button
                                                key={b.id}
                                                type="button"
                                                className={`campaign-ai-brandDropdown__item${isSelected ? ' campaign-ai-brandDropdown__item--selected' : ''}`}
                                                role="option"
                                                aria-selected={isSelected}
                                                onClick={() => {
                                                    setBrandMenuOpen(false);
                                                    onBrandChange?.(b);
                                                }}
                                            >
                                                <span className="campaign-ai-brandDropdown__row">
                                                    <span className="campaign-ai-brandDropdown__avatar" aria-hidden="true">
                                                        {avatarUrl ? <img src={avatarUrl} alt="" /> : getPlatformIcon(b.social_account?.platform)}
                                                    </span>
                                                    <span className="campaign-ai-brandDropdown__meta">
                                                        <span className="campaign-ai-brandDropdown__itemName">{b.name}</span>
                                                        {handle && <span className="campaign-ai-brandDropdown__itemHandle">{handle}</span>}
                                                    </span>
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {children}
            </div>

            <div className="campaign-ai-creation__bottombar">
                {bottomBarContent}
            </div>
        </div>
    )
}

export default AICampaignCreationLayout